/**
 * ProjectInfo Component
 * Displays and edits project information
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { X } from 'lucide-react';
import { API, getApiUrl } from '../../utils/api';

const ProjectInfo = ({ project, currentUser, onProjectDeleted, onProjectUpdated }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [projectName, setProjectName] = useState(project?.name || '');
  const [projectDescription, setProjectDescription] = useState(project?.description || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProjectName(project?.name || '');
    setProjectDescription(project?.description || '');
    
    // Debug: Check if user is creator
    console.log('Project creator:', project?.created_by);
    console.log('Current user:', currentUser.id);
    console.log('Is creator:', project?.created_by === currentUser.id);
  }, [project, currentUser]);

  const handleDeleteProject = async (projectToDelete) => {
    if (!window.confirm(t('projects.confirmDelete', 'Really delete project "{{name}}"? This action cannot be undone.').replace('{{name}}', projectToDelete.name))) return;
    
    try {
      await axios.delete(`${getApiUrl()}/projects/${projectToDelete.id}?user_id=${currentUser.id}`);
      
      // Reload projects and go back to main view
      if (onProjectDeleted) {
        onProjectDeleted();
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert(t('common.errorDeleting', 'Error deleting: ') + (error.response?.data?.detail || error.message));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${getApiUrl()}/projects/${project.id}`, {
        name: projectName,
        description: projectDescription
      });
      
      setEditing(false);
      // Notify parent component to update project data
      if (onProjectUpdated) {
        onProjectUpdated({ ...project, name: projectName, description: projectDescription });
      }
    } catch (error) {
      console.error('Error updating project:', error);
      alert(t('common.error', 'Error saving changes'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h3 className="font-semibold text-gray-800">{t('projects.projectInfo', 'Project information')}</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => editing ? handleSave() : setEditing(true)}
              disabled={saving}
              className={editing ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
            >
              {saving ? t('common.saving', 'Saving...') : editing ? t('common.save', 'Save') : t('common.edit', 'Edit')}
            </Button>
            {(project?.created_by === currentUser.id || currentUser.id === project?.created_by) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDeleteProject(project)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                title={t('projects.deleteProjectHint', 'Delete project (only for creator)')}
              >
                <X className="w-4 h-4 mr-1" />
                {t('common.delete', 'Delete')}
              </Button>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('projects.projectName', 'Project name')}
            </label>
            {editing ? (
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder={t('projects.projectNamePlaceholder', 'Project name...')}
              />
            ) : (
              <p className="text-gray-800 p-2 bg-gray-50 rounded">{project?.name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('projects.description', 'Description')}
            </label>
            {editing ? (
              <textarea
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder={t('projects.descriptionPlaceholder', 'Project description...')}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
              />
            ) : (
              <p className="text-gray-600 p-2 bg-gray-50 rounded">
                {project?.description || t('projects.noDescription', 'No description available')}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <Separator />
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">{t('projects.type', 'Type')}</h3>
          <Badge>{project?.project_type}</Badge>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">{t('projects.createdAt', 'Created on')}</h3>
          <p className="text-gray-600">
            {project?.created_at ? new Date(project.created_at).toLocaleDateString(currentUser?.language || 'de') : t('common.unknown', 'Unknown')}
          </p>
        </div>
      </div>
      
      {editing && (
        <div className="flex flex-wrap gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => {
              setEditing(false);
              setProjectName(project?.name || '');
              setProjectDescription(project?.description || '');
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProjectInfo;
